import {
  CheckCircleFillIcon,
  WSConcernForOthersIcon,
  WSIndependenceIcon,
  WSInitiativeIcon,
  WSInnovationIcon,
  WSLeadershipIcon,
  WSPersistenceIcon,
  WSStressToleranceIcon,
} from "@eworkit/iris";
import { FormikProps } from "formik";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { ITestable } from "@/browser";
import { Header } from "@/components/Header";
import { Section } from "@/components/Section";
import { Text } from "@/components/Text";
import {
  IWorkStyle,
  MAX_WORKSTYLES_POINTS,
  MAX_WORKSTYLES_POINTS_FOR_EACH,
  TWorkStyleType,
} from "@/data/consultant-profiles";
import { Flex, IChangeable } from "@/eworkitComponents";
import { IWithFormikFieldProps, withFormikField } from "@/formikComponents";
import { ProgressRing } from "@/views/ConsultantProfiles/Detail/WorkStylesSection/ProgressRing";
import { SingleWorkStyleEdit } from "@/views/ConsultantProfiles/Detail/WorkStylesSection/SingleWorkstyleEdit";
import { IWorkstylesFormSectionValues } from "@/views/ConsultantProfiles/Detail/WorkStylesSection/WorkstylesForm";

export const validationWorkstylesFormSchema = Yup.object().shape({
  workStyles: Yup.array()
    .of(
      Yup.object().shape({
        score: Yup.number().max(MAX_WORKSTYLES_POINTS_FOR_EACH).min(0),
        type: Yup.string().required(),
      }),
    )
    .test("21-points-test", "21 error", (workstyles: IWorkStyle[]) => {
      const total = workstyles.reduce((sum, workstyle) => (sum += workstyle.score), 0);
      return total === MAX_WORKSTYLES_POINTS;
    }),
});

interface IWorkstyleSectionProps extends IWithFormikFieldProps, IChangeable, ITestable {
  value: IWorkStyle[];
  setFieldTouched: FormikProps<IWorkstylesFormSectionValues>["setFieldTouched"];
  setAllPointsDistributed: (distributed: boolean) => void;
}

export const WORKSTYLES_ICON_MAP: Record<TWorkStyleType, any> = {
  PERFORMER: WSPersistenceIcon,
  LEADER: WSLeadershipIcon,
  TEAM_PLAYER: WSConcernForOthersIcon,
  COOL_HEADED: WSStressToleranceIcon,
  HARD_WORKING: WSInitiativeIcon,
  INDEPENDENT: WSIndependenceIcon,
  PROBLEM_SOLVER: WSInnovationIcon,
};

export const WorkstylesSection: React.FC<IWorkstyleSectionProps> = ({
  onChange,
  value,
  setFieldTouched,
  setAllPointsDistributed,
}) => {
  const { t } = useTranslation("session");

  const pointsLeft = useMemo(() => {
    if (!value || value.length <= 0) {
      return MAX_WORKSTYLES_POINTS;
    } else {
      return MAX_WORKSTYLES_POINTS - value.reduce((acc, curr) => acc + curr.score, 0);
    }
  }, [value]);

  useEffect(() => {
    setAllPointsDistributed(pointsLeft === 0);
  }, [pointsLeft]);

  const handleChange = (type: string) => (score: number) => {
    if (!type) {
      return;
    }

    if (setFieldTouched) {
      setFieldTouched("workStyles", true);
    }

    if (onChange) {
      onChange([...value.filter(w => w.type !== type), { type, score }]);
    }
  };

  const resolution = pointsLeft > 0 ? "NOT_COMPLETED" : "COMPLETED";

  return (
    <Flex direction="column" innerMargin="md">
      <Section
        direction="row"
        innerMargin="md"
        shadow="none"
        background={pointsLeft > 0 ? "background-card-bottom" : "light-green"}
        borderRadius>
        <Flex padding={["md"]} innerPadding="md">
          <Flex>
            {pointsLeft > 0 ? (
              <ProgressRing
                radius={14}
                stroke={2}
                progress={((MAX_WORKSTYLES_POINTS - pointsLeft) / MAX_WORKSTYLES_POINTS) * 100}
              />
            ) : (
              <CheckCircleFillIcon color="g500" size="md" />
            )}
          </Flex>
          <Flex direction="column" innerPadding="sm">
            <Header size="sm">
              {t(`CONSULTANT_PROFILES.DETAIL.WORK_STYLES.DISTRIBUTION.${resolution}.HEADER`, { count: pointsLeft })}
            </Header>
            <Text>{t(`CONSULTANT_PROFILES.DETAIL.WORK_STYLES.DISTRIBUTION.${resolution}.DESCRIPTION`)}</Text>
          </Flex>
        </Flex>
      </Section>

      {Object.entries(WORKSTYLES_ICON_MAP).map(([type, WorkstyleIcon]) => (
        <SingleWorkStyleEdit
          type={type}
          WorkstyleIcon={WorkstyleIcon}
          score={value.find(w => w.type === type)?.score || 0}
          key={type}
          onChange={handleChange(type)}
          pointsLeft={pointsLeft}
        />
      ))}
    </Flex>
  );
};

export const FormikWorkstylesSection = withFormikField(WorkstylesSection);
